import type { PrivateLabel } from '@/models/private-label.model';
import { GenericService } from '@/services/api/generic.service';
import apiClient from '@/services/api/client/apiClient';
import type { AxiosRequestConfig } from 'axios';
import { DomainOrganisationService } from './domain-organisation.service';
import type { Organisation } from '@/models';

export class PrivateLabelService extends GenericService<PrivateLabel> {
  constructor(domainId: string) {
    super(`v1/domains/${domainId}/private-labels`);
  }

  async getPrivateLabelsWithHierarchy(
    organisationId?: string,
    params?: AxiosRequestConfig
  ): Promise<PrivateLabel[]> {
    try {
      const response = await apiClient.get(`${this.endpoint}`, {
        ...params,
        params: {
          ...params?.params,
          include: 'assignments.assignedToDomain,assignments.assignedToOrganization',
          organisation_id: organisationId,
          with_hierarchy: true
        }
      });

      const privateLabels = 'data' in response ? response.data : response as PrivateLabel[];
      return privateLabels;
    } catch (error: any) {
      if (error.response?.status === 403) {
        throw new Error('Access denied to private labels');
      } else if (error.response?.status === 404) {
        throw new Error('Private label not found');
      }
      throw error;
    }
  }

  async getPrivateLabelWithAssignments(privateLabelId: string, params?: AxiosRequestConfig): Promise<PrivateLabel> {
    try {
      const response = await apiClient.get(`${this.endpoint}/${privateLabelId}`, {
        ...params,
        params: {
          ...params?.params,
          include: 'assignments.assignedToDomain,assignments.assignedToOrganization'
        }
      });
      const responseData = 'data' in response ? response.data : response;
      return responseData;
    } catch (error: any) {
      if (error.response?.status === 403) {
        throw new Error('Access denied to private label');
      } else if (error.response?.status === 404) {
        throw new Error('Private label not found');
      }
      throw error;
    }
  }

  async toggleActiveState(privateLabelId: string, params?: AxiosRequestConfig): Promise<{ isActive: boolean }> {
    try {
      const response = await apiClient.post(`${this.endpoint}/${privateLabelId}/toggle`, {}, params);
      const responseData = 'data' in response ? response.data : response;
      return responseData;
    } catch (error: any) {
      if (error.response?.status === 403) {
        throw new Error('Only country level domains can toggle private label active state');
      } else if (error.response?.status === 404) {
        throw new Error('Private label not found');
      }
      throw error;
    }
  }

  async toggleAssignmentState(
    privateLabelId: string,
    assignmentId: string,
    newState: 'enabled' | 'disabled',
    params?: AxiosRequestConfig
  ): Promise<{ state: string }> {
    try {
      const response = await apiClient.patch(
        `${this.endpoint}/${privateLabelId}/assignments/${assignmentId}`,
        { state: newState },
        params
      );
      const responseData = 'data' in response ? response.data : response;
      return responseData;
    } catch (error: any) {
      if (error.response?.status === 403) {
        throw new Error('Access denied to toggle assignment state');
      } else if (error.response?.status === 404) {
        throw new Error('Assignment not found');
      }
      throw error;
    }
  }

  async updatePrivateLabel(
    privateLabelId: string,
    updateData: {
      mls_id: string;
      original_fields?: any;
      replacement_fields: any;
      is_active: boolean;
    },
    params?: AxiosRequestConfig
  ): Promise<PrivateLabel> {
    try {
      const response = await apiClient.put(
        `${this.endpoint}/${privateLabelId}`,
        updateData,
        params
      );
      const responseData = 'data' in response ? response.data : response;
      return responseData;
    } catch (error: any) {
      if (error.response?.status === 403) {
        throw new Error('Access denied to update private label');
      } else if (error.response?.status === 404) {
        throw new Error('Private label not found');
      } else if (error.response?.status === 422) {
        throw new Error(`Validation error: ${error.response.data?.message || 'Invalid data'}`);
      }
      throw error;
    }
  }

  async getOrganisationsForAssignment(domainId: string, params?: AxiosRequestConfig): Promise<Organisation[]> {
    try {
      const organisationService = new DomainOrganisationService(domainId);
      return await organisationService.getOrganisationsWithHierarchy({
        ...params,
        params: {
          ...params?.params,
          direct_only: false,
          all: true
        }
      });
    } catch (error: any) {
      if (error.response?.status === 403) {
        throw new Error('Access denied to organisation details');
      } else if (error.response?.status === 404) {
        throw new Error('Domain not found');
      }
      throw error;
    }
  }

  async createOrganisationAssignments(
    privateLabelId: string,
    assignments: Array<{
      assigned_to_org_id: string;
      state: 'enabled' | 'disabled';
    }>,
    params?: AxiosRequestConfig
  ): Promise<any> {
    try {
      const response = await apiClient.post(
        `${this.endpoint}/${privateLabelId}/assignments`,
        { assignments },
        params
      );
      const responseData = 'data' in response ? response.data : response;
      return responseData;
    } catch (error: any) {
      if (error.response?.status === 403) {
        throw new Error('Access denied to create organisation assignments');
      } else if (error.response?.status === 404) {
        throw new Error('Private label not found');
      } else if (error.response?.status === 422) {
        throw new Error(`Validation error: ${error.response.data?.message || 'Invalid assignment data'}`);
      }
      throw error;
    }
  }

  async createSubdomainAssignment(
    privateLabelId: string,
    domainId: string,
    state: 'enabled' | 'disabled',
    params?: AxiosRequestConfig
  ): Promise<any> {
    try {
      const response = await apiClient.post(
        `${this.endpoint}/${privateLabelId}/assignments`,
        { assignments: [{ assigned_to_domain_id: domainId, state }] },
        params
      );
      const responseData = 'data' in response ? response.data : response;
      return responseData;
    } catch (error: any) {
      if (error.response?.status === 403) {
        throw new Error('Access denied to create subdomain assignment');
      } else if (error.response?.status === 404) {
        throw new Error('Private label not found');
      } else if (error.response?.status === 422) {
        throw new Error(`Validation error: ${error.response.data?.message || 'Invalid assignment data'}`);
      }
      throw error;
    }
  }

  async deletePrivateLabel(privateLabelId: string, params?: AxiosRequestConfig): Promise<void> {
    try {
      await apiClient.delete(`${this.endpoint}/${privateLabelId}`, params);
    } catch (error: any) {
      if (error.response?.status === 403) {
        throw new Error('Access denied to delete private label');
      } else if (error.response?.status === 404) {
        throw new Error('Private label not found');
      }
      throw error;
    }
  }

  async getAssignedOrganisationsForProduct(
    mlsId: string,
    params?: AxiosRequestConfig
  ): Promise<string[]> {
    try {
      const response = await apiClient.get(
        `${this.endpoint}/products/${mlsId}/assigned-organisations`,
        params
      );
      const responseData = 'data' in response ? response.data : response;
      return responseData;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return [];
      }
      throw error;
    }
  }
}
